import { useMemo, useState, type CSSProperties } from 'react'
import {
  ArrowRight,
  BookOpen,
  Brain,
  Code2,
  Eye,
  Flame,
  Goal,
  History,
  ListOrdered,
  Map as MapIcon,
  MessageCircle,
  Package,
  RectangleHorizontal,
  RectangleVertical,
  Scissors,
  Search,
  Sparkles,
  Telescope,
  type LucideIcon,
} from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useChannelFactoryUsage, type FactoryUsage } from '@/api/endpoints/channels'
import { useFactoryCatalog, type FactoryCatalogEntry } from '@/api/endpoints/factories'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { forwardChannel } from '@/lib/routing'
import styles from './FactorySelector.module.scss'

/** Output format axis — drives the format chip filter and is the only
 * grouping the UI exposes after the chip simplification (Hook & Payoff /
 * Lives & POV chips were removed; format is now the only categorical
 * filter the user sees). */
type FactoryFormat = 'short' | 'long'

/** Lucide icon names the backend may emit. Anything unmapped falls
 * back to a neutral placeholder so a typo in DB doesn't break render.
 *
 * Adding a new factory only requires extending this map *if* it picks
 * an icon that's not already on the list — most existing icons are
 * reusable. The DB row's ``icon`` column is the source of truth. */
const ICON_MAP: Record<string, LucideIcon> = {
  flame: Flame,
  map: MapIcon,
  sparkles: Sparkles,
  brain: Brain,
  telescope: Telescope,
  'message-circle': MessageCircle,
  'book-open': BookOpen,
  'list-ordered': ListOrdered,
  package: Package,
  goal: Goal,
  eye: Eye,
  'code-2': Code2,
  scissors: Scissors,
}
const FALLBACK_ICON: LucideIcon = Flame

/** Slugs the frontend has form components for. The selector renders the
 * intersection of this set with the backend catalog — backend can hide
 * a factory by setting ``available = false`` in DB; frontend can soft-
 * disable by removing the slug here (rare; usually backend is the
 * kill-switch). New factories ship by adding a row in DB *and* a form
 * here, then the slug to this set.
 *
 * Typed as a literal union so the ``Link to={`/factory/${slug}`}``
 * narrows to the TanStack route map. */
type SupportedSlug =
  | 'viral-shorts'
  | 'atlas'
  | 'mirage'
  | 'quiz'
  | 'nasa-apod'
  | 'reddit-stories'
  | 'etymology'
  | 'ranked'
  | 'product-review'
  | 'football'
  | 'pov-lives'
  | 'code-levels'
  | 'cortes'
const SUPPORTED_SLUGS: ReadonlySet<SupportedSlug> = new Set<SupportedSlug>([
  'viral-shorts',
  'atlas',
  'mirage',
  'quiz',
  'nasa-apod',
  'reddit-stories',
  'etymology',
  'ranked',
  'product-review',
  'football',
  'pov-lives',
  'code-levels',
  'cortes',
])

interface FactoryOption {
  slug: SupportedSlug
  /** ``video_projects.kind`` — used to look up factory-usage stats. */
  videoKind: string
  icon: LucideIcon
  title: string
  description: string
  format: FactoryFormat
  durationLabel: string
  /** OKLCH hue (0–360) used by ``--h`` to tint icons/borders/hover
   * glows per factory without shipping a new token per kind. */
  hue: number
  available: boolean
  /** Distinct ``video_projects.id`` of this kind across **all**
   * channels and owners. Drives the global popularity sort of the
   * grid; the per-channel "Recentes" banner uses a different signal. */
  totalCount: number
}

function entryToOption(entry: FactoryCatalogEntry): FactoryOption {
  // Caller must pre-filter by SUPPORTED_SLUGS so the cast is sound —
  // any entry that reaches this point has a slug in the typed union.
  return {
    slug: entry.slug as SupportedSlug,
    videoKind: entry.kind,
    icon: ICON_MAP[entry.icon] ?? FALLBACK_ICON,
    title: entry.title,
    description: entry.description,
    format: entry.format,
    durationLabel: entry.duration_label,
    hue: entry.hue,
    available: entry.available,
    totalCount: entry.total_count,
  }
}

/** Collapses the per-channel factory_usage feed into the two things
 * the selector needs to render:
 *  - ``recentOptions`` — the 4 factories most recently produced in the
 *    active channel (banner), each carrying ``lastUsedAt`` so the card
 *    can show "há 2h" / "ontem".
 *  - ``usageCount`` — count per factory, keyed by route slug, so the
 *    grid can sort by "what this channel actually uses".
 *
 * The endpoint already returns rows ordered by ``last_used_at`` DESC,
 * so we just slice the first 4 for the banner. */
function deriveUsage(
  rows: FactoryUsage[] | undefined,
  options: readonly FactoryOption[],
): {
  recentOptions: Array<FactoryOption & { lastUsedAt: string | null; medianGenSeconds: number | null }>
  usageCount: Record<string, number>
  medianBySlug: Record<string, number>
} {
  const usageCount: Record<string, number> = {}
  const medianBySlug: Record<string, number> = {}
  if (!rows || rows.length === 0) return { recentOptions: [], usageCount, medianBySlug }

  const videoKindToOption = new Map<string, FactoryOption>()
  for (const opt of options) {
    if (opt.videoKind) videoKindToOption.set(opt.videoKind, opt)
  }

  const recentOptions: Array<
    FactoryOption & { lastUsedAt: string | null; medianGenSeconds: number | null }
  > = []
  for (const row of rows) {
    const opt = videoKindToOption.get(row.kind)
    if (!opt) continue
    usageCount[opt.slug] = row.count
    if (row.median_generation_seconds != null) {
      medianBySlug[opt.slug] = row.median_generation_seconds
    }
    if (recentOptions.length < 4) {
      recentOptions.push({
        ...opt,
        lastUsedAt: row.last_used_at,
        medianGenSeconds: row.median_generation_seconds,
      })
    }
  }
  return { recentOptions, usageCount, medianBySlug }
}

/** Pretty-print the median generation time for a factory.
 *
 * Always rounds to whole minutes (minimum 1) — sub-minute precision was
 * noisy on cards. Returns "" (falsy) when ``seconds`` is null/0 so the
 * caller hides the hint for factories with no completed run yet. */
function formatGenSeconds(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return ''
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `${minutes}min`
}

function formatAgo(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (seconds < 60) return 'agora'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `há ${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `há ${hours}h`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'ontem'
  if (days < 7) return `há ${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `há ${weeks}sem`
  const months = Math.floor(days / 30)
  if (months < 12) return `há ${months}mês`
  return `há ${Math.floor(days / 365)}a`
}

export function FactorySelector() {
  const { active } = useActiveChannelFromUrl()
  const { data: catalog, isLoading: catalogLoading } = useFactoryCatalog()
  const { data: usageRows } = useChannelFactoryUsage(active?.slug)

  // Render only entries that exist in BOTH backend catalog AND the
  // frontend form map. A row missing on either side is hidden — that
  // covers both backend kill-switch (``available=false``) and frontend
  // gaps (factory shipped backend-first).
  const options = useMemo<readonly FactoryOption[]>(() => {
    if (!catalog) return []
    return catalog.filter((e) => SUPPORTED_SLUGS.has(e.slug as SupportedSlug)).map(entryToOption)
  }, [catalog])

  const { usageCount, medianBySlug, recentOptions: rawRecentOptions } = useMemo(
    () => deriveUsage(usageRows, options),
    [usageRows, options],
  )

  const [search, setSearch] = useState('')
  const [filterFormat, setFilterFormat] = useState<FactoryFormat | null>(null)

  // TikTok channels can only consume 9:16 (short) factories — the
  // platform itself is vertical-only. Hide long-form factories when the
  // active channel is on TikTok so the user can't pick one and end up
  // with a video the platform refuses (or render letterboxed garbage).
  // Returns null when no platform filter applies (YouTube, no channel).
  const platformLockedFormat: FactoryFormat | null =
    active?.platform === 'tiktok' ? 'short' : null

  // Apply the same lock to the "Recentes" banner — otherwise a long-form
  // factory used in a YouTube channel would still appear when the user
  // switches the active channel to TikTok.
  const recentOptions = useMemo(
    () =>
      platformLockedFormat
        ? rawRecentOptions.filter((opt) => opt.format === platformLockedFormat)
        : rawRecentOptions,
    [rawRecentOptions, platformLockedFormat],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return options.filter((opt) => {
      if (platformLockedFormat && opt.format !== platformLockedFormat) return false
      if (filterFormat && opt.format !== filterFormat) return false
      if (q && !opt.title.toLowerCase().includes(q) && !opt.description.toLowerCase().includes(q)) return false
      return true
    })
  }, [options, search, filterFormat, platformLockedFormat])

  // Grid sort: **global** popularity (most-generated factory across
  // every channel) first, available > unavailable, then DB
  // sort_order as the deterministic tiebreaker. The grid does NOT
  // depend on the active channel — only the "Recentes" banner above
  // is per-channel.
  const sorted = useMemo(() => {
    const indexOf = new Map(options.map((o, i) => [o.slug, i] as const))
    return [...filtered].sort((a, b) => {
      if (a.totalCount !== b.totalCount) return b.totalCount - a.totalCount
      if (a.available !== b.available) return a.available ? -1 : 1
      return (indexOf.get(a.slug) ?? 0) - (indexOf.get(b.slug) ?? 0)
    })
  }, [filtered, options])

  const totalAvailable = options.length

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headLeft}>
          <div className={styles.cap}>
            <span className={styles.dot} aria-hidden />
            <span>Factory{active ? ` · ${active.name}` : ''}</span>
          </div>
          <h1 className={styles.title}>Escolha uma fonte</h1>
          <p className={styles.subtitle}>
            {totalAvailable} factories disponíveis · escolha uma para abrir o formulário
          </p>
        </div>
        <label className={styles.search}>
          <Search aria-hidden />
          <input
            type="text"
            placeholder="Buscar factory…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </header>

      {recentOptions.length > 0 ? (
        <section className={styles.banner} aria-labelledby="factory-recents-heading">
          <header className={styles.bannerHead}>
            <div className={styles.bannerLeft}>
              <History aria-hidden className={styles.bannerIcon} />
              <h2 id="factory-recents-heading" className={styles.bannerTitle}>
                Usadas recentemente
              </h2>
              <span className={styles.bannerSub}>
                / últimas {recentOptions.length} de {active?.name ?? 'este canal'}
              </span>
            </div>
          </header>
          <div className={styles.recentsRow}>
            {recentOptions.map((opt) => (
              <RecentCard key={opt.slug} opt={opt} />
            ))}
          </div>
        </section>
      ) : null}

      <div className={styles.controls}>
        <div className={styles.chipRow} role="group" aria-label="Filtros">
          <Chip active={filterFormat === null} onClick={() => setFilterFormat(null)}>
            Todas <span className={styles.tally}>{options.length}</span>
          </Chip>
          <Chip
            active={filterFormat === 'short'}
            onClick={() => setFilterFormat(filterFormat === 'short' ? null : 'short')}
          >
            <RectangleVertical aria-hidden /> 9:16
          </Chip>
          <Chip
            active={filterFormat === 'long'}
            onClick={() => setFilterFormat(filterFormat === 'long' ? null : 'long')}
          >
            <RectangleHorizontal aria-hidden /> 16:9
          </Chip>
        </div>
      </div>

      {catalogLoading ? (
        <div className={styles.empty} role="status">
          Carregando factories…
        </div>
      ) : sorted.length === 0 ? (
        <div className={styles.empty} role="status">
          Nenhuma factory encontrada. Limpe a busca ou troque o filtro.
        </div>
      ) : (
        <div className={styles.grid}>
          {sorted.map((opt) => (
            <Tile
              key={opt.slug}
              opt={opt}
              usage={usageCount[opt.slug] ?? 0}
              medianSeconds={medianBySlug[opt.slug] ?? null}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface ChipProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

function Chip({ active, onClick, children }: ChipProps) {
  return (
    <button
      type="button"
      className={cn(styles.chip, active && styles.chipActive)}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </button>
  )
}

function RecentCard({
  opt,
}: {
  opt: FactoryOption & { lastUsedAt: string | null; medianGenSeconds: number | null }
}) {
  const Icon = opt.icon
  const isDisabled = !opt.available
  const ago = formatAgo(opt.lastUsedAt)
  const style = { '--h': opt.hue } as CSSProperties
  const genHint = formatGenSeconds(opt.medianGenSeconds)

  const inner = (
    <>
      <div className={styles.recentIcon} aria-hidden>
        <Icon />
      </div>
      <div className={styles.recentBody}>
        <h3 className={styles.recentTitle}>{opt.title}</h3>
        <div className={styles.recentMeta}>
          <span className={styles.fmtBadge}>{opt.format === 'short' ? '9:16' : '16:9'}</span>
          {ago ? <span>{ago}</span> : null}
          {genHint ? (
            <span title="Tempo médio de geração nas últimas 30 rodadas deste canal">
              gerado em ~{genHint}
            </span>
          ) : null}
        </div>
      </div>
    </>
  )

  if (isDisabled) {
    return (
      <div className={cn(styles.recentCard, styles.disabled)} style={style} aria-disabled="true">
        {inner}
      </div>
    )
  }

  return (
    <Link to={`/factory/${opt.slug}`} search={forwardChannel} className={styles.recentCard} style={style}>
      {inner}
    </Link>
  )
}

function Tile({
  opt,
  usage,
  medianSeconds,
}: {
  opt: FactoryOption
  usage: number
  medianSeconds: number | null
}) {
  const Icon = opt.icon
  const isDisabled = !opt.available
  const style = { '--h': opt.hue } as CSSProperties
  const genHint = formatGenSeconds(medianSeconds)

  const inner = (
    <>
      <div className={styles.tileIcon} aria-hidden>
        <Icon />
      </div>
      <div className={styles.tileBody}>
        <div className={styles.tileTitleRow}>
          <h3 className={styles.tileTitle}>{opt.title}</h3>
          <span className={styles.tileTag}>{opt.format === 'short' ? '9:16' : '16:9'}</span>
          {usage > 0 ? <span className={styles.usageTag}>×{usage}</span> : null}
        </div>
        <p className={styles.tileDesc}>{opt.description}</p>
        <div className={styles.tileMeta}>
          <span title="Duração do vídeo gerado">saída {opt.durationLabel}</span>
          <span className={styles.tileMetaSpacer} />
          {genHint ? (
            <span
              className={styles.tileMetaGen}
              title="Tempo médio de geração nas últimas 30 rodadas deste canal"
            >
              gerado em ~{genHint}
            </span>
          ) : null}
        </div>
      </div>
      <div className={styles.tileArrow} aria-hidden>
        <ArrowRight />
      </div>
    </>
  )

  if (isDisabled) {
    return (
      <div className={cn(styles.tile, styles.disabled)} style={style} aria-disabled="true">
        {inner}
      </div>
    )
  }

  return (
    <Link to={`/factory/${opt.slug}`} search={forwardChannel} className={styles.tile} style={style}>
      {inner}
    </Link>
  )
}
